import ArticleList from '../../features/articles/ArticleList';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, Box, useMediaQuery, Stack } from '@mui/material';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { ViewHeadlineRounded, ViewListRounded } from '@mui/icons-material';
import ViewModeToggle from '../../components/common/ViewModeToggle';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NoticeCard from '../../components/card/NoticeCard';
import NoticeHeadlineCard from '../../components/card/NoticeHeadlineCard';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';

import SidebarLayout from '../../components/layout/SidebarLayout';
import { noticesApi } from '../../api/notices';
import PageLayout from '../../components/layout/PageLayout';
import PageTitle from '../../components/common/PageTitle';
import NoData from '../../components/common/NoData';
import { useUserStore } from '../../stores/userStore';
import CreateButton from '../../components/button/CreateButton';
import theme from '../../theme';
import SEO from '../../components/common/SEO';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const NoticeContent = () => {
  useTrackPageView({ pageType: PAGE_TYPE.NOTICE_MAIN });
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pinnedNotices, setPinnedNotices] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const hiddenOnly = true;
  const [loadError, setLoadError] = useState('');
  const listRequest = useRef(0);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('noticeViewMode') || 'headline',
  );

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('noticeViewMode', mode);
  };

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const fetchPinnedNotices = useCallback(async () => {
    try {
      const response = await noticesApi.getPinnedNotices();
      setPinnedNotices(response.data);
    } catch (err) {
      console.error('Error fetching pinned notices:', err);
    }
  }, []);

  const fetchNotices = useCallback(
    async (page = 1) => {
      const requestId = ++listRequest.current;
      setLoading(true);
      try {
        const response =
          hiddenOnly && isAdmin
            ? await noticesApi.getHiddenNotices()
            : await noticesApi.getAllNotices(page, 10);
        if (requestId !== listRequest.current) return;
        setNotices(response.data);
        setLoadError('');
        if (hiddenOnly && isAdmin)
          setPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: response.data.length,
            itemsPerPage: response.data.length,
          });
        if (response.meta && response.meta.pagination) {
          setPagination(response.meta.pagination);
        }
      } catch (err) {
        if (requestId !== listRequest.current) return;
        console.error('Error fetching notices:', err);
        setNotices([]);
        setLoadError('공지 목록을 불러오지 못했습니다. 다시 시도해주세요.');
      } finally {
        if (requestId === listRequest.current) setLoading(false);
      }
    },
    [hiddenOnly, isAdmin],
  );

  useEffect(() => {
    fetchPinnedNotices();
  }, [fetchPinnedNotices]);

  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    fetchNotices(page);
    return () => {
      listRequest.current += 1;
    };
  }, [searchParams, fetchNotices]);

  const handlePageChange = (event, newPage) => {
    setSearchParams({ page: newPage.toString() });
  };

  const viewModes = [
    { value: 'headline', Icon: ViewHeadlineRounded },
    { value: 'list', Icon: ViewListRounded },
  ];
  const visiblePinned = hiddenOnly && isAdmin ? [] : pinnedNotices;
  const visibleNotices = notices.filter((item) => isAdmin || !item.isHidden);

  return (
    <PageLayout>
      {isAdmin && (
        <Button onClick={() => navigate('/notice?category=notice')}>
          {hiddenOnly ? '공개 공지 보기' : '숨긴 공지 관리'}
        </Button>
      )}
      {hiddenOnly && isAdmin && (
        <Alert severity="info">
          숨긴 공지입니다. 상세에서 다시 공개할 수 있습니다. 댓글·저장·고정 상태는 보존됩니다.
        </Alert>
      )}
      {loadError && <Alert severity="error">{loadError}</Alert>}
      <SEO title="공지" description={SEO_DESCRIPTIONS.NOTICE} url={`${SITE_URL}/notice`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: '공지' },
            ],
          })}
        </script>
      </Helmet>
      {visibleNotices.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: [...visiblePinned, ...visibleNotices]
                .filter((item) => !item.isHidden)
                .filter((item) => item.imageUrl)
                .map((item, index) => ({
                  '@type': 'ListItem',
                  name: item.title,
                  image: item.imageUrl,
                  url: item.slug
                    ? `${SITE_URL}/notice/${item.slug}`
                    : `${SITE_URL}/notice/${item.id}`,
                  position: index + 1,
                })),
            })}
          </script>
        </Helmet>
      )}
      {/* 타이틀 + 생성 버튼 + xs 보기 아이콘 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: '16px', md: '40px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '16px', md: '20px', lg: '30px' },
          }}
        >
          <PageTitle>공지</PageTitle>
          {isAdmin && (
            <CreateButton
              text="공지"
              onClick={() => navigate('/notice/create')}
              sx={{ fontSize: 14, fontWeight: 500, padding: { xs: '10px 12px', md: '12px 16px' } }}
            />
          )}
        </Box>
        {/* xs ViewMode 아이콘 */}
        {isXs && (
          <ViewModeToggle
            viewMode={viewMode}
            onChangeViewMode={handleChangeViewMode}
            modes={viewModes}
          />
        )}
      </Box>

      <ProgressBar isLoading={loading}>
        {visiblePinned.length === 0 && visibleNotices.length === 0 ? (
          <NoData />
        ) : (
          <>
            {/* md/lg 보기 아이콘 */}
            {!isXs && (
              <ViewModeToggle
                viewMode={viewMode}
                onChangeViewMode={handleChangeViewMode}
                modes={viewModes}
                sx={{ mb: { md: '20px', lg: '30px' } }}
              />
            )}
            <SidebarLayout>
              {/* 카드 목록 */}
              <Stack>
                {/* 고정 공지 */}
                {visiblePinned.map((item, index) => (
                  <Box
                    key={`pinned-${item.id}-${index}`}
                    sx={{
                      px: viewMode === 'headline' ? '16px' : { xs: '10px', md: '16px', lg: '16px' },
                      py: viewMode === 'headline' ? '10px' : { xs: '15px', md: '20px', lg: '30px' },
                      borderTop: index === 0 ? `1px solid ${theme.palette.grey.line}` : 'none',
                      borderBottom: `1px solid ${theme.palette.grey.line}`,
                      backgroundColor: theme.palette.grey.bg,
                    }}
                  >
                    {viewMode === 'headline' ? (
                      <NoticeHeadlineCard notice={item} />
                    ) : (
                      <NoticeCard notice={item} />
                    )}
                  </Box>
                ))}
                {/* 일반 공지 */}
                {visibleNotices.map((item, index) => (
                  <React.Fragment key={`${item.id}-${index}`}>
                    <Box
                      sx={{
                        px:
                          viewMode === 'headline' ? '16px' : { xs: '10px', md: '16px', lg: '16px' },
                        py:
                          viewMode === 'headline' ? '10px' : { xs: '15px', md: '20px', lg: '30px' },
                        borderBottom: `1px solid ${theme.palette.grey.line}`,
                      }}
                    >
                      {viewMode === 'headline' ? (
                        <NoticeHeadlineCard notice={item} />
                      ) : (
                        <NoticeCard notice={item} />
                      )}
                    </Box>
                  </React.Fragment>
                ))}
              </Stack>
              {/*페이지네이션*/}
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
              />
            </SidebarLayout>
          </>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default function Notice() {
  const [params] = useSearchParams();
  const isAdmin = useUserStore((state) => state.isAdmin);
  return isAdmin && params.get('manage') === 'hidden' ? <NoticeContent /> : <ArticleList />;
}
